import {
  Box,
  Drawer,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
  SxProps,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
} from "@mui/material";
import { CommonDrawerStyle, DrawerHeader } from "../../Common/styles/drawer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { v4 as uuid } from "uuid";
import { CloseRounded, InfoOutlined } from "@mui/icons-material";
import http from "../../../utils/http";
import { QuestionLabelMap } from "../../QuestionBuilder/questionTypes";
import { errorToastMessage } from "../../../utils/toast";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import { CriterionNode } from "./nested-dsl-utils";

interface DraggableQuestionProps {
  item: any;
  onSelect: (item: any) => void;
}

const ChipStyle: SxProps = {
  color: "#3F83F8",
  border: "1px solid #3F83F8",
  borderRadius: "16px",
  px: 1.5,
  py: 0.35,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 500,
  fontStyle: "italic",
  mr: 0.5,
};

const DraggableQuestion: React.FC<DraggableQuestionProps> = ({
  item,
  onSelect,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderBottom: "1px solid #e0e0e0",
        p: 1,
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
      }}
      onClick={() => {
        onSelect(item);
      }}
    >
      <Box sx={{ display: "inline", alignItems: "center", gap: 1 }}>
        <MDEditor.Markdown
          wrapperElement={{
            "data-color-mode": "light",
          }}
          source={item.label}
          rehypePlugins={[rehypeSanitize]}
          style={{
            fontWeight: "400",
            fontSize: "14px",
            backgroundColor: "inherit",
          }}
        />
        {item?.varname && (
          <Box sx={ChipStyle} component="span">
            <Typography fontSize={8} fontWeight={"medium"}>
              {item?.varname}
            </Typography>
          </Box>
        )}
        <Box sx={ChipStyle} component="span">
          <Typography fontSize={8} fontWeight={"medium"}>
            {item?.questionTypeLabel}
          </Typography>
        </Box>

        {(item.source === "study" || item.source === "registry") && (
          <Tooltip
            title={
              <>
                {item?.studyName && <div>Study : {item?.studyName}</div>}
                <div>Type: {item?.phaseLabel}</div>
                <div>Form: {item?.formName}</div>
              </>
            }
            arrow
          >
            <IconButton size="small" sx={{ cursor: "default" }}>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

interface FieldSelectionDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedField: CriterionNode;
  studyId?: string;
  onFieldChange: (selection: {
    label: string;
    varname: string;
    questionType: string;
    formName: string;
    questionId: string;
    studyId: string;
    studyName: string;
    questionTypeLabel: string;
    choices?: { id: string; label: string }[];
    source: "study" | "profile" | "registry";
    responseType?: string;
    format?: string;
  }) => void;
  platformType?: "registry" | "study";
}

interface QuestionSelectorProps extends FieldSelectionDrawerProps {
  type?: "study" | "registry";
}

function QuestionSelector({
  open,
  onClose,
  selectedField,
  onFieldChange,
  studyId,
  type,
  platformType,
}: QuestionSelectorProps) {
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState<string>(uuid());
  const [isLoading, setLoading] = useState(false);
  const [searchedList, setSearchedList] = useState<any>({
    count: 0,
    questionsData: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const shouldAllowEmptySearch =
    (platformType === "study" && type === "study") ||
    (platformType === "registry" && type === "registry");

  const fetchData = useCallback(
    async (searchText: string, page: number) => {
      try {
        setLoading(true);
        setSearchedList({ count: 0, questionsData: [] });

        const isCanonical = platformType === "registry" && type === "registry";

        let urlType = "";
        if (platformType === "registry") {
          urlType =
            type === "registry"
              ? "/admin/registry/canonical-questions"
              : "/registry/questions";
        } else {
          urlType = type === "registry" ? "/questions/registry" : "/questions";
        }

        let url = `${urlType}?size=${pageSize}&page=${page}`;

        if (searchText) {
          url += `&search=${searchText}`;
        }

        if (studyId && type !== "registry") {
          url += `&studyId=${studyId}`;
        }

        const res = await http.get(url);

        const rows = isCanonical ? res.data.data.data : res.data.data.rows;
        const totalCount = isCanonical
          ? res.data.data.meta.paginationOptions.totalItems
          : res.data.data.count;

        const formattedData = rows.map((item: any) => {
          if (isCanonical) {
            return {
              id: item.id,
              label: item.label,
              varname: item.varname,
              questionType: item.type,
              questionTypeLabel: QuestionLabelMap[item.type] || "Unknown",
              studyName: "",
              phaseLabel: "",
              formName: "",
              studyId: "",
              choices: item.choices
                ? item.choices.sort((a: any, b: any) => a.position - b.position)
                : [],
              source: type,
              responseType: "",
              format: item.format ?? "",
            };
          }
          const phase = item?.form?.phaseForm?.phase?.category;
          const phaseLabel = phase === "visit" ? "Datasheet" : "Screening";
          return {
            id: item.id,
            label: item.label,
            varname: item.varname,
            questionType: item.type,
            questionTypeLabel: QuestionLabelMap[item.type] || "Unknown",
            studyName: item?.study?.name || "N/A",
            phaseLabel: phaseLabel,
            formName: item?.form?.name,
            studyId: item?.study?.id,
            choices: item.choices
              ? item.choices.sort((a: any, b: any) => a.position - b.position)
              : [],
            source: type,
            responseType: item.responseType ?? "",
            format: item.format ?? "",
          };
        });

        setSearchedList({ count: totalCount, questionsData: formattedData });
        setTotalPages(Math.ceil(totalCount / pageSize) || 1);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    },
    [studyId, type, platformType],
  );

  useEffect(() => {
    const shouldFetch = shouldAllowEmptySearch
      ? searchText === "" || searchText.length >= 3
      : searchText && searchText.length >= 3;

    if (shouldFetch) {
      setCurrentPage(1);
      fetchData(searchText || "", 1);
    } else {
      setSearchedList({ count: 0, questionsData: [] });
      setCurrentPage(1);
      setTotalPages(1);
    }
  }, [searchText, fetchData, shouldAllowEmptySearch]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchData(searchText || "", page);
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val);
      }, 500),
    [setSearchText],
  );

  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
  };

  const onSelect = (item: any) => {
    onFieldChange({
      label: item.label,
      varname: item.varname,
      questionType: item.questionType,
      formName: item.formName,
      studyName: item.studyName,
      questionId: item.id,
      studyId: item.studyId,
      questionTypeLabel: item.questionTypeLabel,
      choices: item.choices,
      source: item.source,
      responseType: item.responseType,
      format: item.format,
    });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "auto",
        pr: 0.5,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
      >
        <TextField
          placeholder="Search"
          style={{ width: "100%" }}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch}>
                  <CloseRounded />
                </IconButton>
              </InputAdornment>
            ),
          }}
          key={searchId}
        />
      </Box>
      {searchText && searchText.length > 0 && searchText.length < 3 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          Type at least 3 characters to search
        </Typography>
      )}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : searchedList.questionsData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {shouldAllowEmptySearch
              ? "No questions found"
              : searchText.length < 3
                ? "Enter a search term to find questions"
                : "No questions found"}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {searchedList.count} result{searchedList.count !== 1 ? "s" : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
          </Box>
          {searchedList.questionsData.map((item: any) => (
            <DraggableQuestion key={item?.id} item={item} onSelect={onSelect} />
          ))}
          {totalPages > 1 && (
            <Box
              sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 2 }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

const ParticipantProfileSelector = ({
  open,
  onClose,
  selectedField,
  onFieldChange,
}: FieldSelectionDrawerProps) => {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let url = `/admin/registry/participants/profile-attributes`;

        const res = await http.get(url);

        const formattedData = res.data.data
          .filter((item: any) => !!QuestionLabelMap[item.questionType])
          .map((item: any) => {
            return {
              id: item.id,
              label: item.label,
              varname: item.varname,
              questionType: item.questionType,
              questionTypeLabel: QuestionLabelMap[item.questionType],
              choices: item.choices
                ? item.choices.sort((a: any, b: any) => a.position - b.position)
                : [],
              source: "profile",
              format: item.format || "",
            };
          });

        setOptions(formattedData);

        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSelect = (item: any) => {
    onFieldChange({
      label: item.label,
      varname: item.varname,
      questionType: item.questionType,
      formName: "",
      studyName: "",
      questionId: item.id,
      studyId: "",
      questionTypeLabel: item.questionTypeLabel,
      choices: item.choices,
      source: item.source,
      format: item.format || "",
    });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "auto",
        pr: 0.5,
      }}
    >
      {loading && options.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : options.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No questions found
          </Typography>
        </Box>
      ) : (
        <>
          {options.map((item) => (
            <DraggableQuestion key={item.id} item={item} onSelect={onSelect} />
          ))}
        </>
      )}
    </Box>
  );
};

const FieldSelectionDrawer = ({
  open,
  onClose,
  selectedField,
  onFieldChange,
  studyId,
  platformType,
}: FieldSelectionDrawerProps) => {
  const [type, setType] = useState<"study" | "profile" | "registry">(
    selectedField?.source ?? "study",
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={CommonDrawerStyle}>
        <DrawerHeader closeDrawer={onClose} title="Select Field" />
        <Box sx={{ py: 2 }}>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, newType) => {
              if (newType !== null) {
                setType(newType);
              }
            }}
            fullWidth
            size="small"
          >
            <ToggleButton value="study">Study</ToggleButton>
            <ToggleButton value="profile">Participant Profile</ToggleButton>
            <ToggleButton value="registry">Registry</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {type === "study" || type === "registry" ? (
          <QuestionSelector
            open={open}
            onClose={onClose}
            selectedField={selectedField}
            onFieldChange={onFieldChange}
            studyId={studyId}
            key={type}
            type={type}
            platformType={platformType}
          />
        ) : (
          <ParticipantProfileSelector
            open={open}
            onClose={onClose}
            selectedField={selectedField}
            onFieldChange={onFieldChange}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default FieldSelectionDrawer;
